import React from 'react';
import {
  StyleSheet,
  Pressable,
  Linking,
  Platform,
  View,
  VirtualizedList
} from 'react-native';
import FontAwesome from 'react-native-vector-icons/FontAwesome';
import { translate } from 'secullum-i18n';
import {
  Card,
  Text,
  Modal,
  ImageButton,
  Message,
  DetailsHeader,
  getTestID,
  Space,
  TextBox
} from 'secullum-react-native-ui';
import { Subscribe } from 'unstated';
import { getTheme } from '../../shared/modules/layout';
import { FuncionarioContainer } from '../containers/FuncionarioContainer';
import { converterParaLowerKebabCase } from '../modules/string';
import { DadosFuncionarioIndicadorCustomizadoGerente } from '../modules/types';

interface ModalIndicadorListaFuncionariosProps {
  funcionarios: Array<DadosFuncionarioIndicadorCustomizadoGerente>;
  corTextoHora: string;
  titulo: string;
  exibirModal: boolean;
  onClose: () => void;
}

interface State {
  alertaFuncionario: string;
  filtro: string;
}

export default class ModalIndicadorListaFuncionarios extends React.Component<
  ModalIndicadorListaFuncionariosProps,
  State
> {
  state: State = {
    alertaFuncionario: '',
    filtro: ''
  };

  handleFiltroChange = (filtro: string) => {
    this.setState({ filtro });
  };

  handlePhoneClick = (celular: string) => {
    if (!celular) {
      this.setState({
        alertaFuncionario: translate(
          'Não há número celular cadastrado para este funcionário'
        )
      });

      return;
    }
    Linking.openURL(`tel:${celular}`);
  };

  renderItem(
    funcionario: DadosFuncionarioIndicadorCustomizadoGerente,
    key: number,
    permitirLigarFuncionariosAusentes: boolean
  ) {
    const { corTextoHora, titulo } = this.props;
    const tituloFormatoKebabCase = converterParaLowerKebabCase(titulo);

    return (
      <Card
        nativeID={`viewFuncionarioLinha-${key}`}
        key={`funcionario-item-${key}`}
        style={styles.cardFuncionario}
      >
        <Card.Section style={styles.containerFuncionarios} key={key}>
          <Pressable
            style={({ pressed }) => [
              { opacity: pressed ? 0.7 : 1 },
              styles.ausenteSection
            ]}
            onPress={() =>
              permitirLigarFuncionariosAusentes &&
              this.handlePhoneClick(funcionario.celular)
            }
          >
            <Text bold flex={1} color={theme.textColor1}>
              {funcionario.nome}
            </Text>

            {funcionario.totalHorasFuncionario != '' && (
              <Text bold color={corTextoHora} style={styles.horasFuncionario}>
                {funcionario.totalHorasFuncionario}
              </Text>
            )}
            {permitirLigarFuncionariosAusentes && (
              <FontAwesome
                name="phone"
                size={25}
                testID={getTestID(
                  `ligar-funcionario-${tituloFormatoKebabCase}-${key}`
                )}
              />
            )}
          </Pressable>
        </Card.Section>
      </Card>
    );
  }

  renderListaFuncionarios() {
    const { funcionarios } = this.props;

    const funcionariosFiltrados = funcionarios.filter(item =>
      item.nome.toLowerCase().includes(this.state.filtro.toLowerCase())
    );

    return funcionarios?.length ? (
      <Subscribe to={[FuncionarioContainer]}>
        {(funcionarioContainer: FuncionarioContainer) => (
          <VirtualizedList
            initialNumToRender={25}
            getItem={(_, index) => funcionariosFiltrados[index]}
            getItemCount={() => funcionariosFiltrados.length}
            keyExtractor={(_, index) => `funcionario-item-${index}`}
            renderItem={({ item, index }) =>
              this.renderItem(
                item,
                index,
                funcionarioContainer.state.dados.dadosPerfilFuncionario
                  .permitirLigarFuncionariosAusentes
              )
            }
          />
        )}
      </Subscribe>
    ) : (
      <View nativeID="viewMensagemNaoHaFuncionarios">
        <Text>{translate('Não há funcionários')}</Text>
      </View>
    );
  }

  render() {
    const { filtro } = this.state;
    const { titulo, exibirModal, funcionarios, onClose } = this.props;

    return (
      <Modal
        visible={exibirModal}
        overlayStyle={Platform.OS === 'web' ? styles.modalWeb : styles.modal}
      >
        <View
          style={Platform.OS === 'web' ? styles.containerWeb : styles.container}
          nativeID="viewListaFuncionariosIndicador"
        >
          <View style={styles.modalIcons}>
            <ImageButton
              style={styles.imageButtonLeft}
              icon="close"
              nativeID="viewListaFuncionariosIndicadorFechar"
              onPress={onClose}
            />
          </View>
          <View style={styles.headerContainer}>
            <DetailsHeader text={titulo} />
          </View>
          {funcionarios?.length > 100 ? (
            <View style={styles.inputWrapper}>
              <Space />
              <TextBox
                value={filtro}
                placeholder={translate('Buscar')}
                onChange={filtro => this.setState({ filtro })}
                labelStyle={styles.inputLabel}
              />
              <FontAwesome
                name="search"
                size={16}
                color="gray"
                style={styles.searchIcon}
              />
              <Space />
            </View>
          ) : null}
          {this.renderListaFuncionarios()}
          <Message
            type="warning"
            message={this.state.alertaFuncionario}
            visible={this.state.alertaFuncionario !== ''}
            onRequestClose={() => this.setState({ alertaFuncionario: '' })}
          />
        </View>
      </Modal>
    );
  }
}
const theme = getTheme();

const styles = StyleSheet.create({
  cardFuncionario: {
    marginVertical: 1.5,
    marginHorizontal: 1.5
  },
  headerContainer: {
    marginBottom: 2
  },
  modal: {
    flex: 1,
    justifyContent: 'center'
  },
  modalWeb: {
    overflow: 'scroll'
  },
  modalIcons: {
    flexDirection: 'row'
  },
  ausenteSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16
  },
  horasFuncionario: {
    marginRight: 10
  },
  containerWeb: {
    margin: 'auto',
    padding: 10,
    width: '90%',
    maxWidth: 366,
    backgroundColor: theme.backgroundColor2,
    borderRadius: 10,
    justifyContent: 'center'
  },
  imageButtonLeft: {
    borderWidth: 0,
    marginLeft: 'auto'
  },
  container: {
    backgroundColor: theme.backgroundColor1,
    marginHorizontal: 16,
    borderRadius: 5,
    maxHeight: '95%',
    padding: 10
  },
  containerFuncionarios: {
    padding: 0
  },
  inputWrapper: {
    position: 'relative'
  },
  inputLabel: {
    display: 'none'
  },
  searchIcon: {
    pointerEvents: 'none',
    position: 'absolute',
    top: Platform.OS === 'web' ? 27 : 30,
    right: 10
  }
});
